import React, { useCallback } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import {
    Divider,
    Flex,
    IconButton,
    LabelTag,
    Link,
    Popover,
    Sheet,
} from '@iris/react-components';
import { XMarkIcon } from '@iris/react-icons';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Box, LoadingSpinner, Text } from 'reaxl';

import vdpVehicleSummaryDuck from '@/ducks/vdp/vdpVehicleSummaryDuck.js';

import { KeywordSearchTrigger } from './KeywordSearchModalContainer';

const fallbackSummaryText = 'I apologize, but I\'m unable to retrieve the vehicle information.';

function ShoppingAssistantPopover({
    handleAssistantClose = () => { },
    headingText = 'What would you like to do?',
    show = false,
    showChatDialog = false,
    suggestedActions = null,
    trigger,
    ...rest
}) {

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isVehicleSummaryLoading = useSelector(vdpVehicleSummaryDuck.selectors.isLoading);
    const vehicleSummary = useSelector(vdpVehicleSummaryDuck.selectors.data)?.summary || fallbackSummaryText;

    const {
        ai_assistant_entry_point: [isAIAssistantEntryPointEnabled, { destination: aiEndpoint }],
    } = useFeatures(['ai_assistant_entry_point']);

    const onHide = useCallback(() => {
        handleAssistantClose();
    }, [handleAssistantClose]);

    const renderChatDialog = (prompt) => (
        <>
            <div className="iris:flex iris:justify-end w-100">
                <Box
                    className="padding-3"
                    backgroundColor="orange-light"
                    borderRadius="chat"
                >
                    <Text size={200}>{prompt}</Text>
                </Box>
            </div>
            <div
                className="iris:my-4 w-100"
            >
                {isVehicleSummaryLoading ? (
                    <LoadingSpinner className="margin-top-1 margin-bottom-2" />
                ) : (
                    <Text size={200}>
                        {
                            // eslint-disable-next-line react/no-danger
                            <div dangerouslySetInnerHTML={{ __html: vehicleSummary }} />
                        }
                    </Text>
                )}

            </div>

        </>
    );

    // Abstract the content/body that will be shared between components
    const content = (
        <div className="iris:flex iris:flex-col iris:grow gap-3">
            <div
                className="iris:flex iris:flex-wrap"
                style={{ maxHeight: '575px', overflow: 'hidden', overflowY: 'auto' }}
            >
                {showChatDialog && renderChatDialog('What\'s great about this vehicle?')}
                {suggestedActions}
                {isAIAssistantEntryPointEnabled && (
                    <Flex className="iris:mt-3 iris:mx-1">
                        <Link
                            className="iris:mr-2"
                            href={`/${aiEndpoint}`}
                            size="sm"
                            target="_blank"
                            weight="bold"
                        >
                            Let AI drive your car search. Ask Autotrader.
                        </Link>
                        <LabelTag text="BETA" />
                    </Flex>
                )}
            </div>
        </div>
    );

    if (isXs) {
        return (
            <div data-cmp="shopping-assistant-popover">
                <Sheet
                    desktopPosition="center"
                    fixedButtons
                    footerContent={(
                        <KeywordSearchTrigger
                            isShoppingAssistant
                            onTriggerClick={handleAssistantClose}
                        />
                    )}
                    hasHeader
                    modal={false}
                    onOpenChange={(open) => {
                        if (!open) {
                            onHide();
                        }
                    }}
                    open={show}
                    title={headingText}
                    trigger={trigger}
                    {...rest}
                >
                    {content}
                </Sheet>
            </div>
        );
    }

    // Desktop: Use Iris Popover component
    return (
        <Popover
            id="shopping-assistant-popover"
            data-cmp="shopping-assistant-popover"
            isOpen={show}
            trigger={trigger}
        >
            <div className="iris:flex iris:items-center iris:relative">
                <Text
                    className="iris:grow iris:text-center"
                    data-cmp="shopping-assist-dt-heading"
                    weight="bold"
                >
                    {headingText}
                </Text>
                <IconButton
                    className="iris:absolute"
                    style={{ right: 0 }}
                    onClick={handleAssistantClose}
                    icon={<XMarkIcon />}
                    size="sm"
                />
            </div>

            <Divider className="iris:my-3" />

            {content}

            <Divider className="iris:my-3" />

            <KeywordSearchTrigger
                isShoppingAssistant
                onTriggerClick={handleAssistantClose}
            />
        </Popover>
    );
}
ShoppingAssistantPopover.propTypes = {
    /**
     * if true, the component will be shown
     * @default false
     */
    show: PropTypes.bool,
    /**
     * A group of CTA's to be rendered in the popover
     */
    suggestedActions: PropTypes.node,
    /**
     * The text to be displayed in the header of the popover
     */
    headingText: PropTypes.string,
    /**
     * Function to handle closing the assistant
     */
    handleAssistantClose: PropTypes.func,
    /**
     * Trigger element for the sheet
     */
    trigger: PropTypes.node,
    /**
     * if true, the chat dialog will be shown
     */
    showChatDialog: PropTypes.bool,
};

export default React.memo(ShoppingAssistantPopover, _isEqual);
